import {
  collectionGroup,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  where,
  type Firestore,
  type QueryDocumentSnapshot
} from 'firebase/firestore';
import type { OrgMembership, OrgRole } from '@finvox/shared/org';

export function userProfilePatchFromMemberships(
  list: OrgMembership[]
): { memberships: OrgMembership[]; defaultOrgId?: string } {
  return {
    memberships: list,
    ...(list.length === 1 ? { defaultOrgId: list[0].orgId } : {})
  };
}

async function membershipsFromMemberDocs(
  db: Firestore,
  memberDocs: QueryDocumentSnapshot[]
): Promise<OrgMembership[]> {
  const list: OrgMembership[] = [];
  for (const m of memberDocs) {
    const memberOrgId = m.ref.parent.parent?.id;
    if (!memberOrgId) continue;
    const orgSnap = await getDoc(doc(db, 'organizations', memberOrgId));
    list.push({
      orgId: memberOrgId,
      orgName: String(orgSnap.data()?.name ?? memberOrgId),
      role: (m.data().role as OrgRole) ?? 'viewer'
    });
  }
  return list;
}

/** Discover member docs via collection group — uid field first, then document id. */
export async function discoverMemberDocsForUid(
  db: Firestore,
  uid: string
): Promise<QueryDocumentSnapshot[]> {
  try {
    const byUid = await getDocs(
      query(collectionGroup(db, 'members'), where('uid', '==', uid))
    );
    if (!byUid.empty) return byUid.docs;
  } catch (e) {
    const code = (e as { code?: string }).code;
    if (code !== 'failed-precondition' && code !== 'permission-denied') {
      console.warn('[syncUserMemberships] members uid query failed', e);
    }
  }

  try {
    const byDocId = await getDocs(
      query(collectionGroup(db, 'members'), where(documentId(), '==', uid))
    );
    return byDocId.docs;
  } catch (e) {
    console.warn('[syncUserMemberships] members documentId query failed', e);
    return [];
  }
}

export async function discoverMembershipsForUid(
  db: Firestore,
  uid: string
): Promise<OrgMembership[]> {
  const memberDocs = await discoverMemberDocsForUid(db, uid);
  if (!memberDocs.length) return [];
  return membershipsFromMemberDocs(db, memberDocs);
}
